import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Plus, ShoppingCart, Wallet, TrendingUp, Users } from "lucide-react";
import WalletCard from "../components/WalletCard";
import { useAuth } from "../contexts/AuthContext";
import { formatMoney, parseMoney } from "../utils/money";

const DashboardHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { walletBalances } = useOutletContext();
  const totalBalance =
    parseMoney(walletBalances.affordable_data_balance) +
    parseMoney(walletBalances.airtime_data_balance);

  // Get user's first name for display
  const userFirstName = user?.first_name || "User";

  // Handle wallet top-up navigation
  const handleWalletTopUp = (walletType) => {
    navigate(`/dashboard/deposits?wallet=${walletType}`);
  };

  return (
    <div className="space-y-8">
      {/* Welcome Card */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-white mb-2 text-nowrap">
            Welcome back, {userFirstName}! 👋
          </h2>
          <p className="text-gray-300 mb-6 text-sm sm:text-base">
            Ready to manage your data business today?
          </p>
          <div className="flex  justify-between items-start ">
            <div className="flex items-center space-x-2 mb-1 sm:mb-0">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#FFD700" }}
              ></div>
              <span className="text-sm text-gray-300">Active</span>
            </div>
            <div className="text-sm text-gray-300">
              Total Balance:{" "}
              <span className="text-yellow-400 font-bold">
                GHS {formatMoney(totalBalance)}
              </span>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400 bg-opacity-10 rounded-full -mr-16 -mt-16"></div>
      </div>

      {/* My Wallets Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-6">My Wallets</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <WalletCard
            title="Affordable Data"
            balance={walletBalances.affordable_data_balance}
            commissionRate={0}
            type="affordable_data"
            onTopUp={handleWalletTopUp}
            gradient="bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700"
            icon={Wallet}
          />
          <WalletCard
            title="Airtime & Data"
            balance={walletBalances.airtime_data_balance}
            commissionRate={parseMoney(walletBalances.commission_rate || 2.5)}
            type="airtime_data"
            onTopUp={handleWalletTopUp}
            gradient="bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700"
            icon={TrendingUp}
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <button
            onClick={() => navigate("/dashboard/deposits")}
            className="p-6 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors text-left"
          >
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
              <Plus className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Add Money</h4>
            <p className="text-sm text-gray-500">
              Top up your wallet instantly
            </p>
          </button>

          <button
            onClick={() => navigate("/dashboard/products/affordable-data")}
            className="p-6 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors text-left"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">
              Buy Affordable Data
            </h4>
            <p className="text-sm text-gray-500">Browse discounted packages</p>
          </button>

          <button
            onClick={() => navigate("/dashboard/products/airtime-data")}
            className="p-6 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors text-left"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
              <ShoppingCart className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">
              Buy instant Airtime & Data
            </h4>
            <p className="text-sm text-gray-500">
              Purchase regular data bundles and airtime
            </p>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Total Sales</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Customers</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Data Sold</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0 GB</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-900">Recent Activity</h3>
        <div className="text-center py-8">
          <p className="text-gray-500">No recent activity to show</p>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
